import { CHOICE, CORE, DELUXE } from 'core/constants/products'
import isEpicPlan from 'utils/pivot/plan/isEpicPlan'
import isQuantumPlan from 'utils/pivot/plan/isQuantumPlan'
import isSureCarePlan from 'utils/pivot/plan/isSureCarePlan'
import isPlanType from 'utils/pivot/plan/isPlanType'
import isBTMPlan from 'utils/pivot/plan/isBTMPlan'
import store from 'utils/store/withMiddleware'
import { propAccessor } from '@hc-frontend/utils'
import is1200Plan from 'utils/pivot/plan/is1200Plan'

function hasOfficeVisitCopay(plan: Plan): boolean {
  return !(
    plan.coreBenefits.officeVisitCopay === '$0' ||
    plan.coreBenefits.officeVisitCopay === 'Subject to deductible and coinsurance'
  )
}

export function hasContraceptiveBenefits(state: string, plan: Plan): boolean {
  const hasPlan = (plan, ...args) => new RegExp(args.join('|')).test(plan.legacyId)
  const nricEpicBase = 'NRIC-EPIC-BAS+'
  const nricEpicBaseRx = 'NRIC-EPIC-BAS-RX+'
  const nricEpicPpo = 'NRIC-EPIC-PPO+'
  const nricEpicPpoRx = 'NRIC-EPIC-PPO-RX+'
  const clicStmClassicStd = 'CLIC-STM-STD+'
  const clicStmClassicDlx = 'CLIC-STM-DLX+'
  const clicStmCore2000 = 'CLIC-STM-COR-2000+'
  const clicQuantum = 'CLIC-STM-QUA+'

  const contraceptiveCoverageByState = {
    AL: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx),
    AR: plan =>
      isBTMPlan(plan) ||
      hasPlan(
        plan,
        nricEpicBaseRx,
        nricEpicPpoRx,
        clicStmClassicStd,
        clicStmClassicDlx,
        clicStmCore2000,
        clicQuantum
      ),
    FL: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx),
    AZ: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx),
    GA: plan => hasPlan(plan, nricEpicBaseRx),
    ID: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx),
    IL: plan => hasPlan(plan, nricEpicBaseRx),
    IN: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx),
    KS: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx),
    KY: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx),
    LA: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx),
    MI: plan => hasPlan(plan, nricEpicBaseRx),
    MO: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx),
    MS: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx),
    MT: plan => hasPlan(plan, nricEpicBaseRx),
    NC: plan => hasPlan(plan, nricEpicBaseRx),
    ND: plan => hasPlan(plan, nricEpicBaseRx),
    NE: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx),
    NV: plan =>
      hasPlan(
        plan,
        nricEpicBase,
        nricEpicBaseRx,
        nricEpicPpo,
        nricEpicPpoRx,
        clicStmClassicStd,
        clicStmClassicDlx,
        clicStmCore2000,
        clicQuantum
      ),
    OH: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx),
    OK: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx),
    SC: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx),
    TN: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx),
    TX: plan =>
      isBTMPlan(plan) ||
      hasPlan(
        plan,
        nricEpicBaseRx,
        clicStmClassicStd,
        clicStmClassicDlx,
        clicStmCore2000
      ),
    VA: plan => hasPlan(plan, nricEpicBaseRx),
    WI: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx),
    WV: plan => hasPlan(plan, nricEpicBaseRx, nricEpicPpoRx)
  }

  return propAccessor(contraceptiveCoverageByState, state, args => false)(plan)
}

export function getBenefits(plan: Plan): Array<string> {
  const planBenefits: string[] = []

  const state = store.getState().hcStorage.location.state_acronym

  planBenefits.push('telemedicine')

  if (is1200Plan(plan, state) || isQuantumPlan(plan) || isEpicPlan(plan)) {
    if (state !== 'KS') {
      planBenefits.push('child')
    }
    planBenefits.push('preventive')
  }

  if (isQuantumPlan(plan)) {
    planBenefits.push('supplemental')
  }

  if (hasOfficeVisitCopay(plan) || isPlanType(plan, CHOICE, DELUXE, CORE)) {
    planBenefits.push('doctor')
  }

  if (plan.drug === 'yes') {
    planBenefits.push('prescription')
  }

  if (hasContraceptiveBenefits(state, plan)) {
    planBenefits.push('contraceptive')
  }

  if (plan.coreBenefits.patientAdvocacyServices) {
    planBenefits.push('patientAdvocacy')
  }

  if (isSureCarePlan(plan)) {
    planBenefits.push('child', 'preventive', 'wellness')
  }

  return planBenefits
}

const labelsMap = {
  telemedicine: 'Free telemedicine',
  child: 'Child immunizations covered',
  preventive: 'Preventive care',
  supplemental: 'Supplemental accident included',
  doctor: 'Doctor visit copay',
  prescription: 'Prescription drugs copay',
  contraceptive: 'Contraceptive coverage',
  patientAdvocacy: 'Patient advocacy services',
  wellness: 'Wellness office visit copay'
}

export function getAllPossibleBenefits(): string[] {
  return Object.keys(labelsMap)
}

export function getBenefitLabel(label: string): string {
  return labelsMap[label]
}
